import {
  isEquationBalanced,
  parseCompound,
  parseEquation,
  type ParsedCompound,
} from './chemicalEquation';
import {
  LEGACY_STP_MOLAR_VOLUME,
  MOLAR_VOLUMES,
  checkNumericAnswer,
  formatSig,
  type AnswerCheck,
  type MolarVolumeCondition,
  type NumericMisconception,
} from './gasLaws';
import { PERIODIC_ELEMENTS } from './periodicTableData';

export interface MolarMassContribution {
  symbol: string;
  name: string;
  atomCount: number;
  atomicMass: number;
  contribution: number;
}

export interface MolarMassResult {
  formula: string;
  molarMass: number;
  contributions: MolarMassContribution[];
}

export interface CalculationResult<T> {
  value: T | null;
  error: string | null;
}

export interface StoichiometryResult {
  equation: string;
  source: ParsedCompound;
  target: ParsedCompound;
  sourceMoles: number;
  targetMoles: number;
  targetGrams: number;
  ratio: { source: number; target: number };
}

export interface ReagentAmount {
  formula: string;
  amount: number;
  unit: 'g' | 'mol';
}

export interface LimitingReagentResult {
  limitingFormula: string;
  reactionExtent: number;
  theoreticalMoles: number;
  theoreticalGrams: number;
  percentYield: number | null;
  target: ParsedCompound;
  reagents: Array<{
    formula: string;
    coefficient: number;
    startingMoles: number;
    requiredMoles: number;
    remainingMoles: number;
    limiting: boolean;
  }>;
}

const ELEMENTS = new Map(
  PERIODIC_ELEMENTS.map((element) => {
    const numericMass = Number(element.atomicMass.match(/\d+(?:\.\d+)?/)?.[0]);
    return [
      element.symbol,
      {
        name: element.symbol === 'Al' ? 'Aluminium' : element.name,
        atomicMass: numericMass,
      },
    ];
  }),
);

function mergeAtoms(
  target: Record<string, number>,
  source: Record<string, number>,
  multiplier: number,
): void {
  Object.entries(source).forEach(([symbol, count]) => {
    target[symbol] = (target[symbol] ?? 0) + count * multiplier;
  });
}

export function calculateMolarMass(input: string): CalculationResult<MolarMassResult> {
  const formula = input.trim().replaceAll(' ', '');
  if (!formula) return { value: null, error: 'Enter a chemical formula.' };
  if (formula.includes('+') || formula.includes('->') || formula.includes('→')) {
    return { value: null, error: 'Enter one substance here, not a whole chemical equation.' };
  }

  const totals: Record<string, number> = {};
  const hydrateParts = formula.split(/[·.]/);
  for (const [index, part] of hydrateParts.entries()) {
    if (!part) return { value: null, error: 'Add a formula on both sides of the hydrate dot.' };
    const multiplierMatch = index > 0 ? part.match(/^(\d+)(?=[A-Z([])/) : null;
    if (index === 0 && /^\d/.test(part)) {
      return {
        value: null,
        error: 'Enter one formula without a reaction coefficient, for example H2O.',
      };
    }
    const multiplier = multiplierMatch ? Number(multiplierMatch[1]) : 1;
    const compoundInput = part.slice(multiplierMatch?.[1].length ?? 0);
    const parsed = parseCompound(compoundInput);
    if (!parsed.compound || parsed.error) {
      return {
        value: null,
        error: `${index > 0 ? 'Hydrate part: ' : ''}${parsed.error?.message ?? 'Formula not recognised.'}`,
      };
    }
    mergeAtoms(totals, parsed.compound.atoms, multiplier);
  }

  const contributions: MolarMassContribution[] = [];
  for (const [symbol, atomCount] of Object.entries(totals)) {
    const element = ELEMENTS.get(symbol);
    if (!element || !Number.isFinite(element.atomicMass)) {
      return { value: null, error: `No classroom atomic mass is available for ${symbol}.` };
    }
    contributions.push({
      symbol,
      name: element.name,
      atomCount,
      atomicMass: element.atomicMass,
      contribution: atomCount * element.atomicMass,
    });
  }

  return {
    value: {
      formula,
      molarMass: contributions.reduce((total, item) => total + item.contribution, 0),
      contributions,
    },
    error: null,
  };
}

function findCompound(compounds: ParsedCompound[], formula: string): ParsedCompound | undefined {
  return compounds.find((compound) => compound.formula === formula);
}

export function calculateStoichiometry(input: {
  equation: string;
  sourceFormula: string;
  targetFormula: string;
  amount: number;
  unit: 'g' | 'mol';
}): CalculationResult<StoichiometryResult> {
  if (!Number.isFinite(input.amount) || input.amount <= 0) {
    return { value: null, error: 'Enter an amount greater than zero.' };
  }
  const parsed = parseEquation(input.equation);
  if (!parsed.equation || parsed.error) {
    return { value: null, error: parsed.error?.message ?? 'Enter a complete chemical equation.' };
  }
  if (!isEquationBalanced(parsed.equation)) {
    return { value: null, error: 'Balance the equation before using its mole ratio.' };
  }

  const compounds = [...parsed.equation.left, ...parsed.equation.right];
  const source = findCompound(compounds, input.sourceFormula);
  const target = findCompound(compounds, input.targetFormula);
  if (!source || !target) {
    return { value: null, error: 'Choose two substances from the balanced equation.' };
  }
  if (source === target) {
    return { value: null, error: 'Choose a different target substance.' };
  }

  const sourceMass = calculateMolarMass(source.formula);
  const targetMass = calculateMolarMass(target.formula);
  if (!sourceMass.value || !targetMass.value) {
    return { value: null, error: sourceMass.error ?? targetMass.error };
  }
  const sourceMoles =
    input.unit === 'mol' ? input.amount : input.amount / sourceMass.value.molarMass;
  const targetMoles = sourceMoles * (target.coefficient / source.coefficient);

  return {
    value: {
      equation: input.equation,
      source,
      target,
      sourceMoles,
      targetMoles,
      targetGrams: targetMoles * targetMass.value.molarMass,
      ratio: { source: source.coefficient, target: target.coefficient },
    },
    error: null,
  };
}

export function equationCompounds(input: string): ParsedCompound[] {
  const parsed = parseEquation(input);
  return parsed.equation ? [...parsed.equation.left, ...parsed.equation.right] : [];
}

export function equationSides(input: string): {
  reactants: ParsedCompound[];
  products: ParsedCompound[];
} {
  const parsed = parseEquation(input);
  return parsed.equation
    ? { reactants: parsed.equation.left, products: parsed.equation.right }
    : { reactants: [], products: [] };
}

export function calculateLimitingReagent(input: {
  equation: string;
  reagents: ReagentAmount[];
  targetFormula: string;
  actualYieldGrams?: number | null;
}): CalculationResult<LimitingReagentResult> {
  const parsed = parseEquation(input.equation);
  if (!parsed.equation || parsed.error) {
    return { value: null, error: parsed.error?.message ?? 'Enter a complete chemical equation.' };
  }
  if (!isEquationBalanced(parsed.equation)) {
    return { value: null, error: 'Balance the equation before comparing reagent amounts.' };
  }

  const target = findCompound(parsed.equation.right, input.targetFormula);
  if (!target) {
    return { value: null, error: 'Choose a product from the right side of the equation.' };
  }
  if (input.reagents.length !== parsed.equation.left.length) {
    return { value: null, error: 'Enter an available amount for every reactant.' };
  }

  const reagentMoles: Array<{
    compound: ParsedCompound;
    startingMoles: number;
    extent: number;
  }> = [];
  for (const compound of parsed.equation.left) {
    const amount = input.reagents.find((item) => item.formula === compound.formula);
    if (!amount || !Number.isFinite(amount.amount) || amount.amount <= 0) {
      return { value: null, error: `Enter an amount greater than zero for ${compound.formula}.` };
    }
    const mass = calculateMolarMass(compound.formula);
    if (!mass.value) return { value: null, error: mass.error };
    const startingMoles =
      amount.unit === 'mol' ? amount.amount : amount.amount / mass.value.molarMass;
    reagentMoles.push({
      compound,
      startingMoles,
      extent: startingMoles / compound.coefficient,
    });
  }

  const limiting = reagentMoles.reduce((smallest, item) =>
    item.extent < smallest.extent ? item : smallest,
  );
  const targetMass = calculateMolarMass(target.formula);
  if (!targetMass.value) return { value: null, error: targetMass.error };
  const theoreticalMoles = limiting.extent * target.coefficient;
  const theoreticalGrams = theoreticalMoles * targetMass.value.molarMass;
  const actualYield = input.actualYieldGrams;
  if (
    actualYield !== undefined &&
    actualYield !== null &&
    (!Number.isFinite(actualYield) || actualYield < 0)
  ) {
    return { value: null, error: 'Actual yield must be zero or a positive number.' };
  }

  return {
    value: {
      limitingFormula: limiting.compound.formula,
      reactionExtent: limiting.extent,
      theoreticalMoles,
      theoreticalGrams,
      percentYield:
        actualYield === undefined || actualYield === null
          ? null
          : (actualYield / theoreticalGrams) * 100,
      target,
      reagents: reagentMoles.map((item) => {
        const requiredMoles = limiting.extent * item.compound.coefficient;
        return {
          formula: item.compound.formula,
          coefficient: item.compound.coefficient,
          startingMoles: item.startingMoles,
          requiredMoles,
          remainingMoles: Math.max(0, item.startingMoles - requiredMoles),
          limiting: item.compound.formula === limiting.compound.formula,
        };
      }),
    },
    error: null,
  };
}

/* ------------------------------------------------------------------------------------------------
 * Senior quantitative extensions (study-chemistry-tools-prd.md §8.1, stoichiometry-lab row):
 * concentration, empirical formula, percentage composition, gas molar volume and atom economy.
 * Atomic masses come from the classroom periodic-table data above. Molar volumes are imported
 * from the Gas Laws Lab so both tools use one reviewed value.
 * ---------------------------------------------------------------------------------------------- */

export const STOICHIOMETRY_EXTENSION_REVIEW = {
  reviewStatus: 'pending-teaching-review',
  sources: [
    'IUPAC standard atomic weights (classroom values in periodicTableData.ts)',
    'Molar volumes 24.8 L mol⁻¹ (SLC, 25 °C, 100 kPa) and 22.7 L mol⁻¹ (STP, 0 °C, 100 kPa) from gasLaws.ts',
    'Atom economy (Trost, 1991; IUPAC green chemistry usage), using balanced-equation masses',
  ],
  conditions:
    'Concentration answers assume the solute dissolves completely in the stated final volume. Gas volumes assume ideal behaviour at the stated SLC or STP conditions.',
} as const;

export interface StoichiometryExample<Input> {
  id: string;
  title: string;
  prompt: string;
  input: Input;
  /** Reviewed answer: a number in the panel's unit, or a formula for the empirical panel. */
  expected: number | string;
}

const PERCENT = 100;
const ML_PER_LITRE = 1000;

export function formatNumber(value: number, digits = 3): string {
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: digits }).format(value);
}

function positive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function molarMassOf(formula: string): CalculationResult<number> {
  const result = calculateMolarMass(formula);
  return result.value
    ? { value: result.value.molarMass, error: null }
    : { value: null, error: result.error };
}

/* ----- Concentration c = n ÷ V ----- */

export type ConcentrationUnknown = 'c' | 'n' | 'V';
export type ConcentrationTreatment = 'correct' | 'ml-as-l' | 'mass-as-moles' | 'inverted';

export interface ConcentrationInput {
  solveFor: ConcentrationUnknown;
  concentration: number;
  amount: number;
  amountUnit: 'mol' | 'g';
  formula: string;
  volume: number;
  volumeUnit: 'L' | 'mL';
}

export interface ConcentrationResult {
  value: number;
  unit: string;
  working: string[];
}

export const CONCENTRATION_UNITS: Record<ConcentrationUnknown, string> = {
  c: 'mol L⁻¹',
  n: 'mol',
  V: 'L',
};

export function calculateConcentration(
  input: ConcentrationInput,
  treatment: ConcentrationTreatment = 'correct',
): CalculationResult<ConcentrationResult> {
  const working: string[] = [];
  const strict = treatment === 'correct';
  let moles = 0;
  let volumeL = 0;
  if (input.solveFor !== 'n') {
    if (strict && !positive(input.amount)) {
      return { value: null, error: 'Enter an amount of solute greater than zero.' };
    }
    if (input.amountUnit === 'g') {
      const mass = molarMassOf(input.formula);
      if (mass.value === null) return { value: null, error: mass.error };
      moles = treatment === 'mass-as-moles' ? input.amount : input.amount / mass.value;
      working.push(
        `n = m ÷ M = ${formatSig(input.amount)} g ÷ ${formatSig(mass.value, 5)} g mol⁻¹ = ${formatSig(input.amount / mass.value)} mol`,
      );
    } else {
      moles = input.amount;
    }
  }
  if (input.solveFor !== 'V') {
    if (strict && !positive(input.volume)) {
      return { value: null, error: 'Enter a solution volume greater than zero.' };
    }
    volumeL =
      input.volumeUnit === 'mL' && treatment !== 'ml-as-l'
        ? input.volume / ML_PER_LITRE
        : input.volume;
    if (input.volumeUnit === 'mL') {
      working.push(
        `V = ${formatSig(input.volume)} mL ÷ 1000 = ${formatSig(input.volume / ML_PER_LITRE)} L`,
      );
    }
  }
  if (input.solveFor !== 'c' && strict && !positive(input.concentration)) {
    return { value: null, error: 'Enter a concentration greater than zero.' };
  }
  const inverted = treatment === 'inverted';
  let value: number;
  if (input.solveFor === 'c') {
    value = inverted ? volumeL / moles : moles / volumeL;
    working.push('c = n ÷ V');
  } else if (input.solveFor === 'n') {
    value = inverted ? input.concentration / volumeL : input.concentration * volumeL;
    working.push('n = c × V');
  } else {
    value = inverted ? input.concentration / moles : moles / input.concentration;
    working.push('V = n ÷ c');
  }
  if (!Number.isFinite(value)) return { value: null, error: 'These values cannot be combined.' };
  working.push(`${input.solveFor} = ${formatSig(value)} ${CONCENTRATION_UNITS[input.solveFor]}`);
  return { value: { value, unit: CONCENTRATION_UNITS[input.solveFor], working }, error: null };
}

type ConcentrationError = Exclude<ConcentrationTreatment, 'correct'>;

const CONCENTRATION_FEEDBACK: Record<ConcentrationError, string> = {
  'ml-as-l':
    'Convert millilitres to litres first: divide by 1000. Concentration is moles per litre, not per millilitre.',
  'mass-as-moles':
    'You used the mass in grams as if it were moles. Divide the mass by the molar mass first: n = m ÷ M.',
  inverted:
    'Check the rearrangement. Concentration is moles per litre: c = n ÷ V, so n = c × V and V = n ÷ c.',
};

export function checkConcentrationAnswer(input: ConcentrationInput, answer: number): AnswerCheck {
  const correct = calculateConcentration(input);
  if (!correct.value) {
    return { status: 'invalid', title: 'Check the data', feedback: correct.error ?? '' };
  }
  const misconceptions = (Object.keys(CONCENTRATION_FEEDBACK) as ConcentrationError[]).flatMap(
    (treatment) => {
      const result = calculateConcentration(input, treatment);
      return result.value
        ? [{ value: result.value.value, feedback: CONCENTRATION_FEEDBACK[treatment] }]
        : [];
    },
  );
  return checkNumericAnswer(
    answer,
    correct.value.value,
    misconceptions,
    `Concentration links moles and litres: ${input.solveFor} = ${formatSig(correct.value.value, 3)} ${correct.value.unit}.`,
    correct.value.unit,
  );
}

/* ----- Empirical formula ----- */

export interface EmpiricalPart {
  symbol: string;
  value: number;
}

export interface EmpiricalInput {
  basis: 'mass' | 'percent';
  parts: EmpiricalPart[];
}

export interface EmpiricalRow {
  symbol: string;
  value: number;
  atomicMass: number;
  moles: number;
  ratio: number;
  count: number;
}

export interface EmpiricalResult {
  formula: string;
  counts: Record<string, number>;
  rows: EmpiricalRow[];
  multiplier: number;
}

/** A mole ratio counts as whole when it is within ±0.1 of an integer after multiplying. */
export const EMPIRICAL_RATIO_TOLERANCE = 0.1;
/** Covers the classroom fractions ½, ⅓, ⅔, ¼ and ¾. Larger multipliers fit almost any noisy data. */
export const MAX_EMPIRICAL_MULTIPLIER = 4;
export const MAX_EMPIRICAL_SUBSCRIPT = 24;
export const PERCENT_TOTAL_TOLERANCE = 2;

function wholeNumberRatio(ratios: number[]): { multiplier: number; counts: number[] } | null {
  for (let multiplier = 1; multiplier <= MAX_EMPIRICAL_MULTIPLIER; multiplier += 1) {
    const scaled = ratios.map((ratio) => ratio * multiplier);
    if (scaled.every((value) => Math.abs(value - Math.round(value)) <= EMPIRICAL_RATIO_TOLERANCE)) {
      const counts = scaled.map((value) => Math.round(value));
      return counts.every((count) => count >= 1 && count <= MAX_EMPIRICAL_SUBSCRIPT)
        ? { multiplier, counts }
        : null;
    }
  }
  return null;
}

function formulaFromCounts(symbols: string[], counts: number[]): string {
  return symbols
    .map((symbol, index) => `${symbol}${counts[index] > 1 ? counts[index] : ''}`)
    .join('');
}

export function calculateEmpiricalFormula(
  input: EmpiricalInput,
  options: { useAtomicMasses: boolean } = { useAtomicMasses: true },
): CalculationResult<EmpiricalResult> {
  if (input.parts.length < 2)
    return { value: null, error: 'Enter data for at least two elements.' };
  const symbols = input.parts.map((part) => part.symbol.trim());
  if (new Set(symbols).size !== symbols.length) {
    return { value: null, error: 'Each element should appear only once.' };
  }
  const rows: EmpiricalRow[] = [];
  for (const part of input.parts) {
    const symbol = part.symbol.trim();
    const element = ELEMENTS.get(symbol);
    if (!element || !Number.isFinite(element.atomicMass)) {
      return {
        value: null,
        error: `“${symbol || '?'}” is not an element symbol with a classroom atomic mass.`,
      };
    }
    if (!positive(part.value)) {
      return {
        value: null,
        error: `Enter a ${input.basis === 'percent' ? 'percentage' : 'mass'} greater than zero for ${symbol}.`,
      };
    }
    const atomicMass = options.useAtomicMasses ? element.atomicMass : 1;
    rows.push({
      symbol,
      value: part.value,
      atomicMass,
      moles: part.value / atomicMass,
      ratio: 0,
      count: 0,
    });
  }
  if (input.basis === 'percent') {
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    if (Math.abs(total - PERCENT) > PERCENT_TOTAL_TOLERANCE) {
      return {
        value: null,
        error: `The percentages add to ${formatSig(total)}%. Percentage composition should total about 100%, so check for a missing element.`,
      };
    }
  }
  const smallest = Math.min(...rows.map((row) => row.moles));
  rows.forEach((row) => {
    row.ratio = row.moles / smallest;
  });
  const whole = wholeNumberRatio(rows.map((row) => row.ratio));
  if (!whole) {
    return {
      value: null,
      error: `The mole ratio ${rows.map((row) => row.ratio.toFixed(2)).join(' : ')} is not within ±${EMPIRICAL_RATIO_TOLERANCE} of a small whole-number ratio, even after multiplying by up to ${MAX_EMPIRICAL_MULTIPLIER}. Check the data rather than forcing a formula.`,
    };
  }
  rows.forEach((row, index) => {
    row.count = whole.counts[index];
  });
  return {
    value: {
      formula: formulaFromCounts(symbols, whole.counts),
      counts: Object.fromEntries(rows.map((row) => [row.symbol, row.count])),
      rows,
      multiplier: whole.multiplier,
    },
    error: null,
  };
}

function sameCounts(a: Record<string, number>, b: Record<string, number>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => a[key] === b[key]);
}

export function checkEmpiricalAnswer(input: EmpiricalInput, answer: string): AnswerCheck {
  const correct = calculateEmpiricalFormula(input);
  if (!correct.value) {
    return { status: 'invalid', title: 'Check the data', feedback: correct.error ?? '' };
  }
  const parsed = parseCompound(answer.trim().replaceAll(' ', ''));
  if (!parsed.compound || parsed.error || parsed.compound.coefficient !== 1) {
    return {
      status: 'invalid',
      title: 'Enter one formula',
      feedback: parsed.error?.message ?? 'Write one formula such as CH2O, without a coefficient.',
    };
  }
  const learner = parsed.compound.atoms;
  const { counts: expected, formula, multiplier, rows } = correct.value;
  const ratioText = rows.map((row) => `${row.symbol} ${row.ratio.toFixed(2)}`).join(' : ');
  if (sameCounts(learner, expected)) {
    return {
      status: 'correct',
      title: 'Correct',
      feedback: `The mole ratio ${ratioText}${multiplier > 1 ? `, multiplied by ${multiplier},` : ''} gives the simplest whole-number formula ${formula}.`,
    };
  }
  const symbols = Object.keys(expected);
  if (Object.keys(learner).sort().join(',') !== [...symbols].sort().join(',')) {
    return {
      status: 'incorrect',
      title: 'Not yet',
      feedback: `Use exactly the elements in the data: ${symbols.join(', ')}.`,
    };
  }
  const factor = learner[symbols[0]] / expected[symbols[0]];
  if (
    Number.isInteger(factor) &&
    factor > 1 &&
    symbols.every((key) => learner[key] === expected[key] * factor)
  ) {
    return {
      status: 'misconception',
      title: 'Not yet — simplify',
      feedback: `That formula is ${factor} × ${formula}. It could be a molecular formula, but an empirical formula uses the simplest whole-number ratio.`,
    };
  }
  // Rounding a ratio such as 1.5 up or down instead of multiplying every ratio.
  const roundedRatio = rows.every((row) => {
    const count = learner[row.symbol];
    return count === Math.max(1, Math.floor(row.ratio)) || count === Math.ceil(row.ratio);
  });
  if (multiplier > 1 && roundedRatio) {
    return {
      status: 'misconception',
      title: 'Not yet — do not round that ratio',
      feedback: `The mole ratio ${ratioText} includes a value that is not close to a whole number (such as 1.5 or 1.33). Multiply every ratio by ${multiplier} instead of rounding.`,
    };
  }
  const massOnly = calculateEmpiricalFormula(input, { useAtomicMasses: false });
  if (massOnly.value && sameCounts(learner, massOnly.value.counts)) {
    return {
      status: 'misconception',
      title: 'Not yet — convert to moles',
      feedback:
        'You compared masses or percentages directly. Atoms combine in mole ratios, so divide each value by its atomic mass first.',
    };
  }
  return {
    status: 'incorrect',
    title: 'Not yet',
    feedback: `Divide each value by its atomic mass, then by the smallest result. The mole ratio is ${ratioText}.`,
  };
}

/* ----- Percentage composition ----- */

export interface PercentCompositionPart {
  symbol: string;
  name: string;
  atomCount: number;
  mass: number;
  percent: number;
}

export interface PercentCompositionInput {
  formula: string;
  symbol: string;
}

export function calculatePercentComposition(
  formula: string,
): CalculationResult<{ molarMass: number; parts: PercentCompositionPart[] }> {
  const result = calculateMolarMass(formula);
  if (!result.value) return { value: null, error: result.error };
  const { molarMass, contributions } = result.value;
  return {
    value: {
      molarMass,
      parts: contributions.map((item) => ({
        symbol: item.symbol,
        name: item.name,
        atomCount: item.atomCount,
        mass: item.contribution,
        percent: (item.contribution / molarMass) * PERCENT,
      })),
    },
    error: null,
  };
}

export function checkPercentCompositionAnswer(
  input: PercentCompositionInput,
  answer: number,
): AnswerCheck {
  const result = calculatePercentComposition(input.formula);
  if (!result.value) {
    return { status: 'invalid', title: 'Check the formula', feedback: result.error ?? '' };
  }
  const part = result.value.parts.find((item) => item.symbol === input.symbol);
  if (!part) {
    return {
      status: 'invalid',
      title: 'Choose an element',
      feedback: `${input.symbol} is not in ${input.formula}.`,
    };
  }
  const { molarMass } = result.value;
  const totalAtoms = result.value.parts.reduce((sum, item) => sum + item.atomCount, 0);
  const oneAtom = part.mass / part.atomCount;
  return checkNumericAnswer(
    answer,
    part.percent,
    [
      {
        value: (oneAtom / molarMass) * PERCENT,
        feedback: `Multiply by the number of ${part.symbol} atoms in the formula (${part.atomCount}) before dividing by the molar mass.`,
      },
      {
        value: (part.atomCount / totalAtoms) * PERCENT,
        feedback:
          'That is the percentage of atoms, not of mass. Use each element’s mass contribution: count × atomic mass.',
      },
      {
        value: (part.mass / (molarMass - part.mass)) * PERCENT,
        feedback:
          'Divide by the molar mass of the whole compound, not by the mass of the other elements.',
      },
      {
        value: part.percent / PERCENT,
        feedback: 'You found the mass fraction. Multiply by 100 to give a percentage.',
      },
    ],
    `${part.atomCount} × ${formatSig(oneAtom, 5)} = ${formatSig(part.mass, 5)} g of ${part.symbol} in ${formatSig(molarMass, 5)} g of ${input.formula}, which is ${formatSig(part.percent, 4)}% by mass.`,
    '%',
  );
}

/* ----- Gas volume from moles: V = n × Vm ----- */

export interface GasVolumeInput {
  amount: number;
  amountUnit: 'mol' | 'g';
  formula: string;
  condition: MolarVolumeCondition;
}

export interface GasVolumeResult {
  moles: number;
  molarMass: number | null;
  molarVolume: number;
  volumeL: number;
}

export function calculateGasVolume(input: GasVolumeInput): CalculationResult<GasVolumeResult> {
  if (!positive(input.amount)) {
    return { value: null, error: 'Enter an amount of gas greater than zero.' };
  }
  let moles = input.amount;
  let molarMass: number | null = null;
  if (input.amountUnit === 'g') {
    const mass = molarMassOf(input.formula);
    if (mass.value === null) return { value: null, error: mass.error };
    molarMass = mass.value;
    moles = input.amount / mass.value;
  }
  const molarVolume = MOLAR_VOLUMES[input.condition].litresPerMol;
  return { value: { moles, molarMass, molarVolume, volumeL: moles * molarVolume }, error: null };
}

export function checkGasVolumeAnswer(input: GasVolumeInput, answer: number): AnswerCheck {
  const result = calculateGasVolume(input);
  if (!result.value) {
    return { status: 'invalid', title: 'Check the data', feedback: result.error ?? '' };
  }
  const { moles, molarVolume, volumeL } = result.value;
  const other: MolarVolumeCondition = input.condition === 'SLC' ? 'STP' : 'SLC';
  const misconceptions: NumericMisconception[] = [
    {
      value: moles * MOLAR_VOLUMES[other].litresPerMol,
      feedback: `You used the ${other} molar volume. This question is at ${MOLAR_VOLUMES[input.condition].label}, so use ${molarVolume} L mol⁻¹.`,
    },
    {
      value: moles * LEGACY_STP_MOLAR_VOLUME.litresPerMol,
      feedback: `22.4 L mol⁻¹ is an older STP value at 101.3 kPa. With the 100 kPa standard, use ${molarVolume} L mol⁻¹ here.`,
    },
    {
      value: moles / molarVolume,
      feedback: 'Multiply by the molar volume: each mole of gas takes up Vm litres, so V = n × Vm.',
    },
  ];
  if (input.amountUnit === 'g') {
    misconceptions.push({
      value: input.amount * molarVolume,
      feedback:
        'Convert the mass to moles first (n = m ÷ M). Molar volume is litres per mole, not per gram.',
    });
  }
  return checkNumericAnswer(
    answer,
    volumeL,
    misconceptions,
    `V = n × Vm = ${formatSig(moles)} mol × ${molarVolume} L mol⁻¹ = ${formatSig(volumeL, 3)} L at ${input.condition}.`,
    'L',
  );
}

/* ----- Atom economy ----- */

export interface AtomEconomyInput {
  equation: string;
  desiredFormula: string;
}

export interface AtomEconomyResult {
  percent: number;
  desiredMass: number;
  reactantMass: number;
  reactants: Array<{ formula: string; coefficient: number; molarMass: number; mass: number }>;
  desired: { formula: string; coefficient: number; molarMass: number };
}

export function calculateAtomEconomy(
  input: AtomEconomyInput,
): CalculationResult<AtomEconomyResult> {
  const parsed = parseEquation(input.equation);
  if (!parsed.equation || parsed.error) {
    return { value: null, error: parsed.error?.message ?? 'Enter a complete chemical equation.' };
  }
  if (!isEquationBalanced(parsed.equation)) {
    return { value: null, error: 'Balance the equation before calculating atom economy.' };
  }
  const desired = findCompound(parsed.equation.right, input.desiredFormula);
  if (!desired) return { value: null, error: 'Choose the desired product from the right side.' };
  const reactants: AtomEconomyResult['reactants'] = [];
  for (const compound of parsed.equation.left) {
    const mass = molarMassOf(compound.formula);
    if (mass.value === null) return { value: null, error: mass.error };
    reactants.push({
      formula: compound.formula,
      coefficient: compound.coefficient,
      molarMass: mass.value,
      mass: mass.value * compound.coefficient,
    });
  }
  const desiredMolar = molarMassOf(desired.formula);
  if (desiredMolar.value === null) return { value: null, error: desiredMolar.error };
  const desiredMass = desiredMolar.value * desired.coefficient;
  const reactantMass = reactants.reduce((sum, item) => sum + item.mass, 0);
  return {
    value: {
      percent: (desiredMass / reactantMass) * PERCENT,
      desiredMass,
      reactantMass,
      reactants,
      desired: {
        formula: desired.formula,
        coefficient: desired.coefficient,
        molarMass: desiredMolar.value,
      },
    },
    error: null,
  };
}

/** Atom economy above this is reported as "every reactant atom ends up in the product". */
const COMPLETE_ATOM_ECONOMY = 99.9;

export function checkAtomEconomyAnswer(input: AtomEconomyInput, answer: number): AnswerCheck {
  const result = calculateAtomEconomy(input);
  if (!result.value) {
    return { status: 'invalid', title: 'Check the equation', feedback: result.error ?? '' };
  }
  const { percent, desiredMass, reactantMass, reactants, desired } = result.value;
  const noCoefficients =
    (desired.molarMass / reactants.reduce((sum, item) => sum + item.molarMass, 0)) * PERCENT;
  return checkNumericAnswer(
    answer,
    percent,
    [
      {
        value: noCoefficients,
        feedback:
          'Use the coefficients: multiply each molar mass by its coefficient in the balanced equation.',
      },
      {
        value: (reactantMass / desiredMass) * PERCENT,
        feedback:
          'The fraction is upside down. Atom economy = mass of desired product ÷ total mass of reactants × 100, so it cannot exceed 100%.',
      },
      {
        value: percent / PERCENT,
        feedback: 'You found the fraction. Multiply by 100 to give a percentage.',
      },
    ],
    `${formatSig(desiredMass, 5)} g of ${desired.formula} from ${formatSig(reactantMass, 5)} g of reactants gives ${formatSig(percent, 4)}%. ${percent > COMPLETE_ATOM_ECONOMY ? 'Every reactant atom ends up in the desired product.' : 'The rest of the mass leaves as by-products.'}`,
    '%',
  );
}

/* ----- Reviewed examples (≥ 8 per panel) ----- */

const conc = (
  solveFor: ConcentrationUnknown,
  values: Partial<ConcentrationInput>,
): ConcentrationInput => ({
  solveFor,
  concentration: 0,
  amount: 0,
  amountUnit: 'mol',
  formula: '',
  volume: 0,
  volumeUnit: 'L',
  ...values,
});

// prettier-ignore
export const CONCENTRATION_EXAMPLES: StoichiometryExample<ConcentrationInput>[] = [
  { id: 'conc-nacl-moles', title: 'NaCl from moles', prompt: '0.500 mol of NaCl is dissolved to make 2.00 L of solution. Find c.', input: conc('c', { amount: 0.5, formula: 'NaCl', volume: 2 }), expected: 0.25 },
  { id: 'conc-nacl-mass', title: 'NaCl from a mass', prompt: '5.85 g of NaCl is dissolved to make 250 mL of solution. Find c.', input: conc('c', { amount: 5.85, amountUnit: 'g', formula: 'NaCl', volume: 250, volumeUnit: 'mL' }), expected: 0.400411 },
  { id: 'conc-half-litre', title: 'Moles in 500 mL', prompt: '0.150 mol of KCl is dissolved to make 500 mL of solution. Find c.', input: conc('c', { amount: 0.15, formula: 'KCl', volume: 500, volumeUnit: 'mL' }), expected: 0.3 },
  { id: 'conc-hcl-aliquot', title: 'HCl aliquot', prompt: 'How many moles of HCl are in 25.0 mL of 0.100 mol L⁻¹ HCl?', input: conc('n', { concentration: 0.1, formula: 'HCl', volume: 25, volumeUnit: 'mL' }), expected: 0.0025 },
  { id: 'conc-naoh-volume', title: 'Volume of NaOH', prompt: 'What volume of 2.00 mol L⁻¹ NaOH contains 0.500 mol of NaOH?', input: conc('V', { concentration: 2, amount: 0.5, formula: 'NaOH' }), expected: 0.25 },
  { id: 'conc-naoh-mass', title: 'NaOH from a mass', prompt: '10.0 g of NaOH is dissolved to make 500 mL of solution. Find c.', input: conc('c', { amount: 10, amountUnit: 'g', formula: 'NaOH', volume: 500, volumeUnit: 'mL' }), expected: 0.500038 },
  { id: 'conc-sulfuric', title: 'Sulfuric acid', prompt: '4.90 g of H2SO4 is dissolved to make 1.00 L of solution. Find c.', input: conc('c', { amount: 4.9, amountUnit: 'g', formula: 'H2SO4', volume: 1 }), expected: 0.0499633 },
  { id: 'conc-copper-sulfate', title: 'Copper(II) sulfate', prompt: 'How many moles of CuSO4 are in 200 mL of 0.0500 mol L⁻¹ solution?', input: conc('n', { concentration: 0.05, formula: 'CuSO4', volume: 200, volumeUnit: 'mL' }), expected: 0.01 },
  { id: 'conc-glucose-volume', title: 'Glucose volume', prompt: 'What volume of 0.200 mol L⁻¹ glucose solution contains 9.00 g of C6H12O6?', input: conc('V', { concentration: 0.2, amount: 9, amountUnit: 'g', formula: 'C6H12O6' }), expected: 0.249784 },
];

// prettier-ignore
export const EMPIRICAL_EXAMPLES: StoichiometryExample<EmpiricalInput>[] = [
  { id: 'emp-ch2o', title: 'Sugar-like compound', prompt: 'A compound is 40.0% C, 6.7% H and 53.3% O by mass.', input: { basis: 'percent', parts: [{ symbol: 'C', value: 40 }, { symbol: 'H', value: 6.7 }, { symbol: 'O', value: 53.3 }] }, expected: 'CH2O' },
  { id: 'emp-al2o3', title: 'Aluminium oxide (×2)', prompt: '2.70 g of aluminium combines with 2.40 g of oxygen.', input: { basis: 'mass', parts: [{ symbol: 'Al', value: 2.7 }, { symbol: 'O', value: 2.4 }] }, expected: 'Al2O3' },
  { id: 'emp-fe2o3', title: 'Iron oxide (×2)', prompt: 'An iron oxide is 69.9% Fe and 30.1% O by mass.', input: { basis: 'percent', parts: [{ symbol: 'Fe', value: 69.9 }, { symbol: 'O', value: 30.1 }] }, expected: 'Fe2O3' },
  { id: 'emp-fe3o4', title: 'Magnetite (×3)', prompt: 'Magnetite is 72.4% Fe and 27.6% O by mass.', input: { basis: 'percent', parts: [{ symbol: 'Fe', value: 72.4 }, { symbol: 'O', value: 27.6 }] }, expected: 'Fe3O4' },
  { id: 'emp-ch4', title: 'A simple hydrocarbon', prompt: 'A hydrocarbon is 75.0% C and 25.0% H by mass.', input: { basis: 'percent', parts: [{ symbol: 'C', value: 75 }, { symbol: 'H', value: 25 }] }, expected: 'CH4' },
  { id: 'emp-ch2', title: 'An alkene', prompt: 'An alkene is 85.6% C and 14.4% H by mass.', input: { basis: 'percent', parts: [{ symbol: 'C', value: 85.6 }, { symbol: 'H', value: 14.4 }] }, expected: 'CH2' },
  { id: 'emp-c3h8', title: 'Propane (×3)', prompt: 'A fuel gas is 81.8% C and 18.2% H by mass.', input: { basis: 'percent', parts: [{ symbol: 'C', value: 81.8 }, { symbol: 'H', value: 18.2 }] }, expected: 'C3H8' },
  { id: 'emp-mgo', title: 'Burning magnesium', prompt: '0.486 g of magnesium forms an oxide containing 0.320 g of oxygen.', input: { basis: 'mass', parts: [{ symbol: 'Mg', value: 0.486 }, { symbol: 'O', value: 0.32 }] }, expected: 'MgO' },
  { id: 'emp-no2', title: 'A nitrogen oxide', prompt: 'A brown gas is 30.4% N and 69.6% O by mass.', input: { basis: 'percent', parts: [{ symbol: 'N', value: 30.4 }, { symbol: 'O', value: 69.6 }] }, expected: 'NO2' },
  { id: 'emp-na2so4', title: 'Three elements', prompt: 'A salt is 32.4% Na, 22.6% S and 45.1% O by mass.', input: { basis: 'percent', parts: [{ symbol: 'Na', value: 32.4 }, { symbol: 'S', value: 22.6 }, { symbol: 'O', value: 45.1 }] }, expected: 'Na2SO4' },
];

// prettier-ignore
export const PERCENT_COMPOSITION_EXAMPLES: StoichiometryExample<PercentCompositionInput>[] = [
  { id: 'pc-water-o', title: 'Oxygen in water', prompt: 'What percentage of the mass of H2O is oxygen?', input: { formula: 'H2O', symbol: 'O' }, expected: 88.8103 },
  { id: 'pc-co2-c', title: 'Carbon in CO2', prompt: 'What percentage of the mass of CO2 is carbon?', input: { formula: 'CO2', symbol: 'C' }, expected: 27.2921 },
  { id: 'pc-nacl-na', title: 'Sodium in salt', prompt: 'What percentage of the mass of NaCl is sodium?', input: { formula: 'NaCl', symbol: 'Na' }, expected: 39.3395 },
  { id: 'pc-caco3-ca', title: 'Calcium in limestone', prompt: 'What percentage of the mass of CaCO3 is calcium?', input: { formula: 'CaCO3', symbol: 'Ca' }, expected: 40.0436 },
  { id: 'pc-nh4no3-n', title: 'Nitrogen in a fertiliser', prompt: 'What percentage of the mass of NH4NO3 is nitrogen?', input: { formula: 'NH4NO3', symbol: 'N' }, expected: 34.999 },
  { id: 'pc-fe2o3-fe', title: 'Iron in haematite', prompt: 'What percentage of the mass of Fe2O3 is iron?', input: { formula: 'Fe2O3', symbol: 'Fe' }, expected: 69.9437 },
  { id: 'pc-glucose-c', title: 'Carbon in glucose', prompt: 'What percentage of the mass of C6H12O6 is carbon?', input: { formula: 'C6H12O6', symbol: 'C' }, expected: 40.0021 },
  { id: 'pc-hydrate-cu', title: 'Copper in a hydrate', prompt: 'What percentage of the mass of CuSO4·5H2O is copper?', input: { formula: 'CuSO4·5H2O', symbol: 'Cu' }, expected: 25.4513 },
  { id: 'pc-ammonium-sulfate-n', title: 'Nitrogen in (NH4)2SO4', prompt: 'What percentage of the mass of (NH4)2SO4 is nitrogen?', input: { formula: '(NH4)2SO4', symbol: 'N' }, expected: 21.2012 },
];

// prettier-ignore
export const GAS_VOLUME_EXAMPLES: StoichiometryExample<GasVolumeInput>[] = [
  { id: 'gv-co2-slc', title: 'One mole at SLC', prompt: 'What volume does 1.00 mol of CO2 occupy at SLC?', input: { amount: 1, amountUnit: 'mol', formula: 'CO2', condition: 'SLC' }, expected: 24.8 },
  { id: 'gv-o2-stp', title: 'Half a mole at STP', prompt: 'What volume does 0.500 mol of O2 occupy at STP?', input: { amount: 0.5, amountUnit: 'mol', formula: 'O2', condition: 'STP' }, expected: 11.35 },
  { id: 'gv-co2-mass', title: 'CO2 from a mass', prompt: 'What volume does 4.40 g of CO2 occupy at SLC?', input: { amount: 4.4, amountUnit: 'g', formula: 'CO2', condition: 'SLC' }, expected: 2.47949 },
  { id: 'gv-o2-mass', title: 'O2 from a mass', prompt: 'What volume does 3.20 g of O2 occupy at STP?', input: { amount: 3.2, amountUnit: 'g', formula: 'O2', condition: 'STP' }, expected: 2.27014 },
  { id: 'gv-n2-slc', title: 'Nitrogen at SLC', prompt: 'What volume does 0.250 mol of N2 occupy at SLC?', input: { amount: 0.25, amountUnit: 'mol', formula: 'N2', condition: 'SLC' }, expected: 6.2 },
  { id: 'gv-nh3-mass', title: 'Ammonia from a mass', prompt: 'What volume does 1.70 g of NH3 occupy at SLC?', input: { amount: 1.7, amountUnit: 'g', formula: 'NH3', condition: 'SLC' }, expected: 2.47548 },
  { id: 'gv-ch4-mass', title: 'Methane at STP', prompt: 'What volume does 8.00 g of CH4 occupy at STP?', input: { amount: 8, amountUnit: 'g', formula: 'CH4', condition: 'STP' }, expected: 11.3196 },
  { id: 'gv-h2-mass', title: 'Hydrogen at SLC', prompt: 'What volume does 2.016 g of H2 occupy at SLC?', input: { amount: 2.016, amountUnit: 'g', formula: 'H2', condition: 'SLC' }, expected: 24.8 },
  { id: 'gv-he-stp', title: 'Helium at STP', prompt: 'What volume does 0.0400 mol of He occupy at STP?', input: { amount: 0.04, amountUnit: 'mol', formula: 'He', condition: 'STP' }, expected: 0.908 },
];

// prettier-ignore
export const ATOM_ECONOMY_EXAMPLES: StoichiometryExample<AtomEconomyInput>[] = [
  { id: 'ae-fermentation', title: 'Fermentation', prompt: 'Glucose is fermented to ethanol. Find the atom economy for ethanol.', input: { equation: 'C6H12O6 -> 2C2H5OH + 2CO2', desiredFormula: 'C2H5OH' }, expected: 51.1435 },
  { id: 'ae-lime', title: 'Making lime', prompt: 'Limestone is heated to make lime. Find the atom economy for CaO.', input: { equation: 'CaCO3 -> CaO + CO2', desiredFormula: 'CaO' }, expected: 56.0288 },
  { id: 'ae-haber', title: 'Haber process', prompt: 'Nitrogen and hydrogen form ammonia. Find the atom economy for NH3.', input: { equation: 'N2 + 3H2 -> 2NH3', desiredFormula: 'NH3' }, expected: 100 },
  { id: 'ae-peroxide', title: 'Oxygen from peroxide', prompt: 'Hydrogen peroxide decomposes. Find the atom economy for O2.', input: { equation: '2H2O2 -> 2H2O + O2', desiredFormula: 'O2' }, expected: 47.0365 },
  { id: 'ae-blast-furnace', title: 'Blast furnace', prompt: 'Iron(III) oxide is reduced by carbon monoxide. Find the atom economy for Fe.', input: { equation: 'Fe2O3 + 3CO -> 2Fe + 3CO2', desiredFormula: 'Fe' }, expected: 45.8278 },
  { id: 'ae-hydration', title: 'Hydration of ethene', prompt: 'Ethene reacts with steam. Find the atom economy for ethanol.', input: { equation: 'C2H4 + H2O -> C2H5OH', desiredFormula: 'C2H5OH' }, expected: 100 },
  { id: 'ae-steam-reforming', title: 'Steam reforming', prompt: 'Methane reacts with steam. Find the atom economy for hydrogen.', input: { equation: 'CH4 + H2O -> CO + 3H2', desiredFormula: 'H2' }, expected: 17.7579 },
  { id: 'ae-zinc-acid', title: 'Zinc and acid', prompt: 'Zinc reacts with hydrochloric acid. Find the atom economy for hydrogen.', input: { equation: 'Zn + 2HCl -> ZnCl2 + H2', desiredFormula: 'H2' }, expected: 1.45774 },
  { id: 'ae-baking-soda', title: 'Heating baking soda', prompt: 'Sodium hydrogencarbonate is heated. Find the atom economy for Na2CO3.', input: { equation: '2NaHCO3 -> Na2CO3 + H2O + CO2', desiredFormula: 'Na2CO3' }, expected: 63.0836 },
];
